import AppPaths from '../../../../AppPaths';
import { AppState } from '../../../../types';
import { billingAccountQuery } from '../../../../graphql-operations';
import { logInternalError } from '../../../../lib/errors';
import Button from '../../../../components/Button';
import { inAppCancellation as events } from '../../../../lib/analytics/events';

import * as gqlTypes from '../../../../types/gql-op-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import { typedWindow } from '../../../../lib/Window';
import { useHistory, useLocation } from 'react-router-dom';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useZendeskCancellationTicket } from '../hooks/useZendesk';
import { maybeTruncatePetName } from '../util/helpers';
import { BaseInAppCancellationState } from '../util/types';
import CancelConfirmationModal from '../../components/CancelConfirmationModal';
import { zendeskCancellationReason } from '../../../../types/gql-op-types';
import { DIGITAL_SUPPORT_LINE_NAME, PHONE_SUPPORT_NUMBER } from '../../../../lib/util/phoneSupport';
import ErrorScreen from '../../components/ErrorScreen/ErrorScreen';

export interface ContactCxState extends BaseInAppCancellationState {
  selectedCancelReason: gqlTypes.zendeskCancellationReason;
}

export default function ContactCx() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<ContactCxState>();
  const { selectedCancelReason, subscriptionCategory } = location.state;
  const showBillingCopy = selectedCancelReason === zendeskCancellationReason.BILLING_ERROR;
  const { device } = usePurchaseSubscriptionContext();
  const session = useSelector((state: AppState) => state.session);
  const petName = device.pet?.name ? maybeTruncatePetName(device.pet.name) : t('your_dog');
  events.pageLoad({ petId: device.pet!.id, subscriptionCategory }, 'Contact CX');

  const { mutation, loading } = useZendeskCancellationTicket({
    analyticsEvent: events.cancelFromContactPage,
    selectedCancelReason,
    device,
    subscriptionCategory,
  });

  const { data: accountData } = useQuery<gqlTypes.billingAccount>(billingAccountQuery, {
    onError(err) {
      logInternalError(err);
    },
  });

  // TODO: replace this chat with Decagon?
  // https://app.asana.com/0/1207101507783044/1208333496745341/f
  typedWindow.adaEmbed?.reset({
    greeting: '6504880420176cf9b0200f76', // This greeting value is the answer ID associated with a chatbot flow set up by CX,
    metaFields: {
      customername: `${accountData?.currentUser.firstName ?? ''}${
        // eslint-disable-next-line no-useless-concat
        accountData?.currentUser.lastName ? ' ' + accountData.currentUser.lastName : ''
      }`,
      email: session?.email || '',
      'SubCancel-Reason': selectedCancelReason,
    },
  });

  const showChatButton = useSelector(
    (state: AppState) => state.config.siteConfig.showInAppCancellationChatButton ?? false,
  );

  const showUpgradeVersions: string[] = ['S1', 'S2'];
  const deviceModel = device.currentCollarModel || '';
  const offerS3Upgrade = showUpgradeVersions.includes(deviceModel);

  if (loading) {
    return loading;
  }

  if (!device.subscription?.id) {
    return <ErrorScreen />;
  }

  const handleRetentionOffers = () => {
    events.cancelFromContactPage({
      petId: device.pet?.id,
      cancellationReason: selectedCancelReason,
      subscriptionCategory,
    });

    if (offerS3Upgrade) {
      history.push({
        pathname: AppPaths.Subscription.Upgrade(device.moduleId),
        state: { selectedCancelReason, subscriptionCategory },
        search: window.location.search,
      });
    } else {
      throw new Error('Something went wrong.');
    }
  };

  const handleCancel = () => {
    events.cancelFromContactPage({
      petId: device.pet?.id,
      cancellationReason: selectedCancelReason,
      subscriptionCategory,
    });
    mutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription!.id,
          cancellationReason: selectedCancelReason,
          additionalText: undefined,
        },
      },
    });
  };

  const componentToReturn = () => {
    function ContinueButton() {
      return (
        <Button onClick={handleRetentionOffers} className={styles.button} tertiary={true}>
          {t('continue_with_cancellation')}
        </Button>
      );
    }

    function CancelModal() {
      return (
        <CancelConfirmationModal onContinue={() => handleCancel()} pageName={t('contact_cx')} petId={device.pet!.id} />
      );
    }

    if (showBillingCopy) {
      return <CancelModal />;
    } else if (offerS3Upgrade) {
      return <ContinueButton />;
    } else {
      return <CancelModal />;
    }
  };

  const phoneIconUrl = '/phone_icon.png';
  const chatIconUrl = '/chat_icon.png';

  const getHeaderCopy = () => {
    return showBillingCopy
      ? t('maybe_we_can_help_fix_your_billing_issue')
      : t('maybe_we_can_help_fix_the_issue_with_pet_name_collar', { petName });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.contactCxBack({ petId: device.pet?.id })}
          closeButtonAnalyticsEvent={() => events.contactCxClose({ petId: device.pet?.id })}
        />
        <div className={styles.subscriptionContainer}>
          <h1>{getHeaderCopy()}</h1>

          <div className={styles.content}>
            <p>
              {t(
                'our_customer_success_agents_are_experts_at_fixing_issue_type_issues_can_we_help_resolve_your_problem',
                {
                  issueType:
                    selectedCancelReason === zendeskCancellationReason.BILLING_ERROR
                      ? t('billing_lowercase')
                      : t('collar_lowercase'),
                },
              )}
            </p>

            <div className={styles.inlineButtons}>
              {showChatButton && (
                <Button
                  className={styles.button}
                  disabled={typedWindow.adaEmbed === undefined} // Should not happen in production.
                  onClick={() => {
                    events.chatWithCx({
                      petId: device.pet?.id,
                      cancellationReason: selectedCancelReason,
                      subscriptionCategory,
                    });
                    typedWindow.adaEmbed?.toggle();
                  }}
                >
                  <img src={chatIconUrl} alt={t('chat')} className={styles.buttonIcon} />
                  {t('chat')}
                </Button>
              )}
              <Button
                className={styles.button}
                secondary={true}
                onClick={() => {
                  events.callCx({
                    petId: device.pet?.id,
                    cancellationReason: selectedCancelReason,
                    subscriptionCategory,
                  });
                  const phoneSupportDeeplinkUrl = `fi://phone_support?digitalSupportLineName=${DIGITAL_SUPPORT_LINE_NAME}&phoneSupportNumber=${PHONE_SUPPORT_NUMBER}`;
                  window.open(phoneSupportDeeplinkUrl);
                }}
              >
                <img src={phoneIconUrl} alt={t('call')} className={styles.buttonIcon} />
                {t('call')}
              </Button>
            </div>
          </div>
        </div>
        <div className={styles.buttonContainer}>{componentToReturn()}</div>
      </div>
    </>
  );
}
